import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, X, Loader2, Calendar, Clock, MapPin, Users, Edit, Check } from "lucide-react";

const ScheduleManager = () => {
  const { authAxios } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    date: "",
    title: "",
    drills: "",
    time: "",
    location: "",
    max_capacity: "20"
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedule`);
      setSchedules(response.data);
    } catch (e) {
      toast.error("Failed to fetch schedules");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        date: formData.date,
        title: formData.title,
        drills: formData.drills.split(',').map(d => d.trim()).filter(d => d),
        time: formData.time,
        location: formData.location,
        max_capacity: parseInt(formData.max_capacity)
      };
      
      await authAxios.post('/schedule', payload);
      toast.success("Session added successfully!");
      setShowForm(false);
      resetForm();
      fetchSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add session");
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule({
      ...schedule,
      drills: schedule.drills.join(', '),
      max_capacity: schedule.max_capacity.toString()
    });
    setShowForm(false);
  };

  const handleSaveEdit = async () => {
    if (!editingSchedule) return;
    
    try {
      const payload = {
        date: editingSchedule.date,
        title: editingSchedule.title,
        drills: editingSchedule.drills.split(',').map(d => d.trim()).filter(d => d),
        time: editingSchedule.time,
        location: editingSchedule.location,
        max_capacity: parseInt(editingSchedule.max_capacity)
      };
      
      await authAxios.put(`/schedule/${editingSchedule.id}`, payload);
      toast.success("Session updated!");
      setEditingSchedule(null);
      fetchSchedules();
    } catch (e) {
      toast.error("Failed to update session");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this session?")) return;
    
    try {
      await authAxios.delete(`/schedule/${id}`);
      toast.success("Session deleted");
      if (editingSchedule?.id === id) setEditingSchedule(null);
      fetchSchedules();
    } catch (e) {
      toast.error("Failed to delete session");
    }
  };

  const resetForm = () => {
    setFormData({
      date: "",
      title: "",
      drills: "",
      time: "",
      location: "",
      max_capacity: "20"
    });
  };

  // Fix timezone issue - parse date as local date
  const formatDate = (dateString) => {
    // Parse YYYY-MM-DD as local date (not UTC)
    const parts = dateString.split('-');
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Get day of week for display
  const getDayOfWeek = (dateString) => {
    const parts = dateString.split('-');
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-400" />
          Training Schedule ({schedules.length})
        </h2>
        <button
          onClick={() => { setShowForm(!showForm); setEditingSchedule(null); }}
          className="cyber-btn py-2 px-4 text-xs border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black"
          data-testid="add-schedule-btn"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD SESSION"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4 border-cyan-400/30" data-testid="schedule-form">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-cyan-400/30">
            <Plus className="w-5 h-5 text-cyan-400" />
            <span className="font-orbitron text-sm text-cyan-400">ADD NEW SESSION</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="bg-void-black border-white/10"
                data-testid="schedule-date-input"
              />
              <p className="font-rajdhani text-[10px] text-gray-500">
                Any day of the week - weekends included!
              </p>
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Time *</Label>
              <Input
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                required
                placeholder="6:00 PM"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Max Capacity</Label>
              <Input
                type="number"
                value={formData.max_capacity}
                onChange={(e) => setFormData({...formData, max_capacity: e.target.value})}
                placeholder="20"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Session Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                placeholder="Speed & Agility"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Location *</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                required
                placeholder="Main Field"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Drills (comma separated) *</Label>
              <Input
                value={formData.drills}
                onChange={(e) => setFormData({...formData, drills: e.target.value})}
                required
                placeholder="40-Yard Dash, Ladder Drills"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" className="cyber-btn py-2 px-6 text-xs border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black" data-testid="save-schedule-btn">
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                SAVE SESSION
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Schedule List */}
      <div className="space-y-3">
        {schedules.map(schedule => {
          const isEditing = editingSchedule?.id === schedule.id;
          
          return (
            <div key={schedule.id} className={`cyber-card p-4 ${isEditing ? 'ring-2 ring-neon-green' : ''}`} data-testid={`schedule-item-${schedule.id}`}>
              {isEditing ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-neon-green/30">
                    <Edit className="w-4 h-4 text-neon-green" />
                    <span className="font-orbitron text-xs text-neon-green">EDITING SESSION</span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Date</Label>
                      <Input
                        type="date"
                        value={editingSchedule.date}
                        onChange={(e) => setEditingSchedule({...editingSchedule, date: e.target.value})}
                        className="bg-void-black border-white/10 h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Time</Label>
                      <Input
                        value={editingSchedule.time}
                        onChange={(e) => setEditingSchedule({...editingSchedule, time: e.target.value})}
                        className="bg-void-black border-white/10 h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Capacity</Label>
                      <Input
                        type="number"
                        value={editingSchedule.max_capacity}
                        onChange={(e) => setEditingSchedule({...editingSchedule, max_capacity: e.target.value})}
                        className="bg-void-black border-white/10 h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Title</Label>
                      <Input
                        value={editingSchedule.title}
                        onChange={(e) => setEditingSchedule({...editingSchedule, title: e.target.value})}
                        className="bg-void-black border-white/10 h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Location</Label>
                      <Input
                        value={editingSchedule.location}
                        onChange={(e) => setEditingSchedule({...editingSchedule, location: e.target.value})}
                        className="bg-void-black border-white/10 h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Drills</Label>
                      <Input
                        value={editingSchedule.drills}
                        onChange={(e) => setEditingSchedule({...editingSchedule, drills: e.target.value})}
                        className="bg-void-black border-white/10 h-8 text-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <button onClick={() => setEditingSchedule(null)} className="text-gray-500 hover:text-white text-xs flex items-center gap-1">
                      <X className="w-4 h-4" /> Cancel
                    </button>
                    <button onClick={handleSaveEdit} className="cyber-btn py-2 px-4 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black">
                      <Check className="w-4 h-4 mr-1 inline" /> SAVE
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-shrink-0 text-center md:text-left md:w-32">
                    <div className="font-orbitron text-lg text-white">{formatDate(schedule.date)}</div>
                    <div className="font-rajdhani text-xs text-cyan-400">{getDayOfWeek(schedule.date)}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-orbitron text-sm text-white mb-2">{schedule.title}</h3>
                    <div className="flex flex-wrap gap-4 text-xs font-rajdhani text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-cyan-400" />
                        {schedule.time}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-neon-pink" />
                        {schedule.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-neon-blue" />
                        {schedule.attendees?.length || 0}/{schedule.max_capacity}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {schedule.drills.map((drill, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-white/5 text-gray-400 font-rajdhani">
                          {drill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(schedule)}
                      className="text-gray-500 hover:text-neon-green transition-colors p-2 hover:bg-neon-green/10 rounded"
                      title="Edit session"
                      data-testid={`edit-schedule-${schedule.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(schedule.id)}
                      className="text-gray-500 hover:text-red-500 transition-colors p-2 hover:bg-red-500/10 rounded"
                      title="Delete session"
                      data-testid={`delete-schedule-${schedule.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {schedules.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-schedule-state">
          <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No sessions scheduled. Add your first training session!</p>
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;
